import { Component, Host, h, Prop, Element, EventEmitter, Event, Method, Watch } from '@stencil/core';
import { treeNodeOpenAnimation } from './animations/open';
import { Animation } from '@ionic/core';
import { treeNodeCloseAnimation } from './animations/close';
import { AnchorInterface, generateAnchorAttributes } from '../../../utils/elements-interface';
import { DISABLE_ANIMATION_VALUE } from '../../../utils/animations';

const CHILD_SLOT = 'child';

/**
 * @slot - Tree node content
 * @slot child - Tree node child. Should be used in tree-node elements. Ex:  `<sy-tree-node slot='child'> (...)</sy-tree-node>`
 */
@Component({
  tag: 'sy-tree-node',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class TreeNode implements AnchorInterface {
  /**
   * Internal boolean to indicate when the property `expanded` was modified externally.
   */
  private internalExpanded: boolean;
  private typeDefinedByUser: boolean;
  private currentAnimation: Animation | undefined;
  private childElement: HTMLElement | undefined;

  /**
   * MutationObserver used in handleSlotChange function
   */
  private slotObserver: MutationObserver;

  @Element() el: HTMLSyTreeNodeElement;

  /**
   * Indicate if the node is an inner node (has child) or leaf.
   * It is calculated automatically, however it can be setted to force a behaviour, e.g. lazy load nodes.
   */
  @Prop({ mutable: true, reflect: true }) type: 'inner-node' | 'leaf';

  /**
   * Indicates the deep level the node has in the tree. The first level is 0.
   */
  @Prop({ mutable: true, reflect: true }) level: number;

  /**
   * Indicate if the node is expanded.
   */
  @Prop({ mutable: true, reflect: true }) expanded: boolean;

  /**
   * List item HTML <a> href atribute.
   */
  @Prop({ reflect: true }) href: string | undefined;

  /**
   * List item HTML <a> download attribute.
   */
  @Prop() download: string | undefined;

  /**
   * List item HTML <a> rel attribute.
   */
  @Prop() rel: string | undefined;

  /**
   * List item HTML <a> target attribute.
   */
  @Prop() target: '_blank' | '_self' | '_parent' | '_top';

  @Watch('expanded')
  expandedWatcher(): Promise<void> | undefined {
    if (this.expanded === this.internalExpanded) return;

    if (this.expanded) {
      return this.expand();
    }

    return this.collapse();
  }

  /**
   * Indicate if the node is active.
   */
  @Prop({ mutable: true, reflect: true }) active: boolean;

  /**
   * Indicate if the node is disabled.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * Optional custom animation to replace the default and be played when the component starts opening.
   * If 'none', the tree-node won't have open animation.
   */
  @Prop() openAnimation?: ((elements: { node: HTMLElement; child: HTMLElement }) => Animation) | string;

  /**
   * Optional custom animation to replace the default and be played when the component starts closing.
   * If 'none', the tree-node won't have close animation.
   */
  @Prop() closeAnimation?: ((elements: { node: HTMLElement; child: HTMLElement }) => Animation) | string;

  /**
   * Disables all animations
   */
  @Prop() disableAnimations = false;

  /**
   * Emitted after a tree node is expanded.
   */
  @Event() syTreeNodeDidExpand: EventEmitter<boolean>;

  /**
   * Emitted after a tree node is activated.
   */
  @Event() syTreeNodeDidActivate: EventEmitter<boolean>;

  componentWillLoad() {
    this.typeDefinedByUser = !!this.type;
    this.handleSlotChange();
  }

  componentWillRender() {
    if (this.typeDefinedByUser === false) {
      this.type = this.el.querySelector(`[slot='${CHILD_SLOT}']`) ? 'inner-node' : 'leaf';
    }
    this.level =
      this.el.parentElement && this.el.parentElement.localName === 'sy-tree-node'
        ? (this.el.parentElement as HTMLSyTreeNodeElement).level + 1
        : 0;
    this.active = this.active && !this.disabled;
  }

  componentDidLoad() {
    if (this.active && this.type === 'leaf') {
      this.syTreeNodeDidActivate.emit(this.active);
    }
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;
    this.slotObserver?.disconnect();
  }

  /**
   *  Returns the imediate child tree-nodes.
   */
  @Method()
  async getChildren(): Promise<NodeListOf<HTMLSyTreeNodeElement> | null> {
    if (this.type === 'leaf') {
      return null;
    }

    return this.el.querySelectorAll(`:scope > sy-tree-node`);
  }

  /**
   *  Returns the parent tree-node.
   */
  @Method()
  getParent(): Promise<HTMLSyTreeNodeElement | null> {
    if (this.el.parentElement?.nodeName === this.el.nodeName) {
      return Promise.resolve(this.el.parentElement as HTMLSyTreeNodeElement);
    }

    return Promise.resolve(null);
  }

  /**
   *  Activate the tree-node.
   */
  @Method()
  async activate(emit = true): Promise<void> {
    if (!this.active && !this.disabled) {
      this.active = true;
      if (emit) {
        this.syTreeNodeDidActivate.emit(this.active);
      }
    }
  }

  /**
   *  Deactivate the tree-node.
   */
  @Method()
  async deactivate(emit = true): Promise<void> {
    if (this.active) {
      this.active = false;
      if (emit) {
        this.syTreeNodeDidActivate.emit(this.active);
      }
    }
  }

  /**
   *  Toggle expanded state.
   */
  @Method()
  async toggleExpand(emit = true, instant = false): Promise<void> {
    if (this.type === 'inner-node' && !this.disabled) {
      if (this.expanded) {
        return this.collapse(emit, instant);
      }

      return this.expand(emit, instant);
    }
  }

  /**
   *  Expand the tree-node.
   * If 'instant' is true, the tree node won't have open animation.
   */
  @Method()
  async expand(emit = true, instant = false): Promise<void> {
    const isAlreadyExpanded = this.expanded && this.internalExpanded;
    if (this.type === 'inner-node' && !isAlreadyExpanded && !this.disabled) {
      this.internalExpanded = true;
      this.expanded = true;
      if (this.currentAnimation) {
        this.currentAnimation.stop();
      }

      if (!instant && !this.disableAnimations) {
        this.currentAnimation = this.getOpenAnimation();
        if (this.currentAnimation) {
          await this.currentAnimation.play();
        }
      }

      if (emit) {
        this.syTreeNodeDidExpand.emit(this.expanded);
      }
    }
  }

  /**
   *  Collapse the tree-node.
   * If 'instant' is true, the tree node won't have close animation.
   */
  @Method()
  async collapse(emit = true, instant = false): Promise<void> {
    const isAlreadyCollapsed = !this.expanded && !this.internalExpanded;
    if (this.type === 'inner-node' && !isAlreadyCollapsed) {
      this.internalExpanded = false;

      if (this.currentAnimation) {
        this.currentAnimation.stop();
      }

      if (!instant && !this.disableAnimations) {
        this.currentAnimation = this.getCloseAnimation();
        if (this.currentAnimation) {
          await this.currentAnimation.play();
        }
      }

      this.expanded = false;
      if (emit) {
        this.syTreeNodeDidExpand.emit(this.expanded);
      }
    }
  }

  private handleSlotChange(): void {
    this.slotObserver = new MutationObserver(() => {
      if (this.typeDefinedByUser) return;
      this.type = this.el.querySelector(`[slot='${CHILD_SLOT}']`) ? 'inner-node' : 'leaf';
    });

    this.slotObserver.observe(this.el, {
      childList: true,
      characterData: true,
    });
  }

  private handleNodeCLick(e): void {
    const hasDataDisableToggle = e.target.hasAttribute('data-sy-tree-disable-toggle');
    if (hasDataDisableToggle) {
      return;
    }

    if (this.type === 'leaf') {
      this.handleLeafClick();
      return;
    }

    this.toggleExpand();
  }

  private getOpenAnimation() {
    if (typeof this.openAnimation === 'string') {
      if (this.openAnimation === DISABLE_ANIMATION_VALUE) {
        return;
      }
      if (this.childElement) {
        return treeNodeOpenAnimation({ node: this.el, child: this.childElement });
      }
      return;
    }
    const animationBuilder = this.openAnimation || treeNodeOpenAnimation;
    if (this.childElement) {
      return animationBuilder({ node: this.el, child: this.childElement });
    }
  }

  private getCloseAnimation() {
    if (typeof this.closeAnimation === 'string') {
      if (this.closeAnimation === DISABLE_ANIMATION_VALUE) {
        return;
      }
      if (this.childElement) {
        return treeNodeCloseAnimation({ node: this.el, child: this.childElement });
      }
      return;
    }
    const animationBuilder = this.closeAnimation || treeNodeCloseAnimation;
    if (this.childElement) {
      return animationBuilder({ node: this.el, child: this.childElement });
    }
  }

  private handleLeafClick(): void {
    this.activate();
  }

  render() {
    return (
      <Host>
        <sy-list-item
          class="tree-node-list-item"
          onClick={(e) => this.handleNodeCLick(e)}
          active={this.active}
          disabled={this.disabled}
          {...generateAnchorAttributes(this)}
        >
          {this.type === 'inner-node' &&
            (this.expanded ? (
              <sy-icon class="expand-icon" name="arrow-down-1"></sy-icon>
            ) : (
              <sy-icon class="expand-icon" name="arrow-right-1"></sy-icon>
            ))}
          <slot></slot>
        </sy-list-item>
        <div
          class="child"
          ref={(ref) => (this.childElement = ref)}
          style={{ '--sy-tree-node-level': this.level.toString() }}
        >
          <slot name={CHILD_SLOT}></slot>
        </div>
      </Host>
    );
  }
}
